import { createFileRoute } from '@tanstack/react-router'
import { HammerIcon, PackageIcon, StoreIcon, WarehouseIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStock } from '@/api/stock'
import type { StockRow } from '@/api/stock'
import { useWorkspace } from '@/api/workspace'
import { CsvExportButton } from '@/components/csv-export/csv-export-button'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatQty } from '@/components/materials/pool-format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { limitingPartLabel } from '@/components/stock/limiting-part'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// Stock view (UI-SCREENS I-01, 05 §Stock view): the searchable table per Variant — Buildable,
// Gudang (Home), Stok di kanal per Sales Channel and Total. Buildable is a separate column and is
// never added into Total, since Variants share WIP (#55); Total is Home + every channel. A row
// opens P-10.
//
// Every Sales Channel gets its own figure in the row's meta line, never collapsed into one
// (owner decision 2026-09-23, #176 Q1); on a phone the meta line wraps. An archived channel keeps
// its figure, marked archived, until its stock is 0.
//
// Listed: every non-archived Variant of a non-archived Product, plus anything that still holds
// stock, so nothing with stock is hidden — an archived Variant keeps its row with its badge
// (#170 Q4, G1). Owner and Staff both see this screen.
export const Route = createFileRoute('/w/$slug/stock/')({
  component: StockView,
  staticData: { breadcrumb: 'stock.title' },
})

function StockView() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const [search, setSearch] = useState('')
  const [pages, setPages] = useState(1)
  const [loaded, setLoaded] = useState<StockRow[]>([])
  const stock = useStock(slug, { q: search.trim(), page: pages })
  // The CSV is Owner only (permissions.md): the button is hidden for Staff and the endpoint
  // refuses them. It exports every page of the current search, not just the rows loaded here.
  const isOwner = useWorkspace(slug).data?.role === 'owner'

  // A new search restarts at page 1, so the pages already shown are dropped.
  function searchFor(term: string) {
    setSearch(term)
    setPages(1)
    setLoaded([])
  }

  const rows = pages === 1 ? (stock.data?.items ?? []) : [...loaded, ...(stock.data?.items ?? [])]

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('stock.title')}</h1>
        {isOwner ? <CsvExportButton slug={slug} list="stock" query={{ q: search.trim() }} /> : null}
      </div>

      <Input
        type="search"
        className="h-11 sm:max-w-xs"
        aria-label={t('stock.search')}
        placeholder={t('stock.search')}
        value={search}
        onChange={(event) => searchFor(event.target.value)}
      />

      {stock.isPending && rows.length === 0 ? (
        <LoadingSkeleton />
      ) : stock.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : rows.length === 0 ? (
        <RecordListEmpty>{t('stock.empty')}</RecordListEmpty>
      ) : (
        <>
          <RecordList>
            {rows.map((row) => (
              <StockRowItem key={row.variant.id} slug={slug} row={row} />
            ))}
          </RecordList>
          {stock.data?.next_page ? (
            <Button
              variant="outline"
              className="h-11 self-start"
              disabled={stock.isFetching}
              onClick={() => {
                setLoaded(rows)
                setPages((current) => current + 1)
              }}
            >
              {t(stock.isFetching ? 'stock.loadingMore' : 'stock.loadMore')}
            </Button>
          ) : null}
        </>
      )}
    </section>
  )
}

// One Variant's row: the Variant opens P-10, Buildable, Gudang and one fact per Sales Channel sit
// in the meta line (an archived channel says so), Total is the figure.
function StockRowItem({ slug, row }: { slug: string; row: StockRow }) {
  const { t } = useTranslation()
  const { variant, stock } = row
  const substitutes = stock.buildable !== null && stock.with_substitutes !== null ? stock.with_substitutes - stock.buildable : 0
  const hint =
    stock.buildable_reason !== null
      ? t(`stock.reasons.${stock.buildable_reason}`, { defaultValue: '' })
      : substitutes > 0
        ? t('stock.withSubstitutes', { count: substitutes })
        : ''
  const limiting = stock.buildable_limiting ? limitingPartLabel(t, stock.buildable_limiting) : ''
  const description = [hint, limiting].filter(Boolean).join(' · ')

  return (
    <RecordListItem
      title={
        <RecordLink to="/w/$slug/variants/$variantId" params={{ slug, variantId: String(variant.id) }}>
          {variant.sku} · {variant.name}
        </RecordLink>
      }
      badges={
        <>
          {variant.archived ? <Badge variant="outline">{t('stock.archived')}</Badge> : null}
          {variant.needs_color ? <Badge variant="outline">{t('stock.needsColor')}</Badge> : null}
        </>
      }
      meta={[
        { icon: PackageIcon, label: t('batches.columns.product'), value: variant.product.name },
        {
          icon: HammerIcon,
          label: t('stock.columns.buildable'),
          value: `${t('stock.columns.buildable')} ${stock.buildable === null ? '—' : formatQty(stock.buildable)}`,
        },
        { icon: WarehouseIcon, label: t('stock.columns.home'), value: `${t('stock.columns.home')} ${formatQty(stock.home)}` },
        ...stock.channels.map((channel) => ({
          icon: StoreIcon,
          label: `${t('stock.columns.channels')} · ${channel.name}`,
          value: `${channel.name} ${formatQty(channel.qty)}${channel.archived ? ` (${t('stock.archived')})` : ''}`,
        })),
      ]}
      description={description || undefined}
      figure={formatQty(stock.total)}
      caption={t('stock.columns.total')}
    />
  )
}
